/**
 * Ranking da viagem (em Mais): pódio e lista do Top Comprador, títulos da
 * viagem e medalhas por tipo de gasto — no layout da referência do PO
 * (teste físico, item 29), com o selo "Top comprador", a coroa e o pódio
 * ilustrado (`public/ilustracoes/`, de `design/`).
 *
 * Tudo vem agregado do servidor. A tela nunca vê uma despesa: só totais e
 * quem leva cada título. Todo mundo participa: o "Aparecer no ranking" saiu
 * (item 31).
 */
import { featureEnabled, formatMoney, RANKING_CATEGORY_LABELS, RANKING_MAIN_TITLE, RANKING_TITLE_LABELS, type RankingCategory, type RankingTitle } from '@nv/core';
import { useState } from 'react';

import { useTrip } from '../../trip/TripContext';
import { Etiqueta } from '../../ui/Etiqueta';
import { Icon, type IconName } from '../../ui/icons';
import { TOM_DA_CATEGORIA, TOM_DO_TITULO } from './etiquetas';
import { Card, EmptyState, ErrorState, ScreenHeader, SectionLabel, Skeleton } from '../../ui/primitives';
import { FotoAvatar } from '../perfil/FotoAvatar';
import { type Holder, type Ranking, useRanking } from './api';
import { Classificacao, primeiroNome } from './Classificacao';
import { DestaqueSheet } from './DestaqueSheet';
import { EvolucaoDoRanking } from './EvolucaoDoRanking';

function Avatar({ nome, cor, url, tamanho = 34, classe = '' }: { nome: string; cor?: string | undefined; url?: string | null | undefined; tamanho?: number; classe?: string }) {
  return <FotoAvatar nome={nome} url={url} cor={cor} tamanho={tamanho} className={classe} />;
}

/** O que está aberto em destaque: um título ou uma medalha por tipo de gasto. */
type Destaque = { tipo: 'titulo'; key: RankingTitle } | { tipo: 'medalha'; category: RankingCategory };

export function RankingScreen() {
  const { trip, tripId, members } = useTrip();
  const ligado = featureEnabled(trip.features, 'ranking');
  const ranking = useRanking(tripId, ligado);
  const [destaque, setDestaque] = useState<Destaque | null>(null);
  const corDe = (memberId: string) => members.find((m) => m.memberId === memberId)?.familyColor;
  const fotoDe = (memberId: string) => members.find((m) => m.memberId === memberId)?.avatarUrl;

  if (!ligado) {
    return (
      <section className="screen">
        <EmptyState ilustracao="ranking" title="Ranking desligado" description="Quem administra a viagem desligou esta funcionalidade." />
      </section>
    );
  }

  const r = ranking.data;
  const dinheiro = (v: number) => formatMoney(v, r?.currency ?? trip.baseCurrency);
  const donos = (hs: Holder[], unidade: (n: number) => string) =>
    hs.length === 0 ? (
      <div className="rk__dono rk__dono--vazio"><span className="rk__dono-vazio" aria-hidden="true" /><span className="rk__dono-conta">ninguém ainda</span></div>
    ) : (
      hs.map((h) => (
        <div className="rk__dono" key={h.memberId}>
          <Avatar nome={h.name} cor={corDe(h.memberId)} url={fotoDe(h.memberId)} tamanho={24} />
          <span className="rk__dono-texto"><span className="rk__dono-nome">{primeiroNome(h.name)}</span><span className="rk__dono-conta mono">{unidade(h.value)}</span></span>
        </div>
      ))
    );

  return (
    <section className="screen rk" data-testid="ranking">
      <ScreenHeader
        title="Ranking da viagem"
      />

      {ranking.isLoading && (
        <>
          <Skeleton height={320} raio="hero" />
          <Skeleton height={120} count={2} />
        </>
      )}
      {ranking.isError && <ErrorState title="Não foi possível carregar" description="Verifique a conexão e tente de novo." onRetry={() => void ranking.refetch()} />}

      {r && (
        <>
          {/* Refino final: um card só — a etiqueta no alto (sem rótulo escrito
              repetindo), o pódio e a lista a partir do 4º. */}
          <Card variant="raised" className="rk__top" data-testid="rk-top">
            <Classificacao
              linhas={r.people.map((p) => ({ memberId: p.memberId, name: p.name, value: p.totalMinor, position: p.position }))}
              valor={dinheiro}
              meuMemberId={r.me.memberId}
              apenasDepoisDoPodio
              selo={<Etiqueta nome={RANKING_MAIN_TITLE.nome} icone="trofeu" tom="coral" className="nv-pop rk__etiqueta-top" data-testid="rk-etiqueta" />}
            />
          </Card>

          <SectionLabel>EVOLUÇÃO</SectionLabel>
          <EvolucaoDoRanking />

          <SectionLabel>TÍTULOS DA VIAGEM</SectionLabel>
          <div className="rk__grade" data-testid="rk-titulos">
            {r.titles.map((t) => {
              const l = RANKING_TITLE_LABELS[t.key];
              const tem = t.holders.length > 0;
              // Toque abre o título em destaque: pódio + lista completa na
              // unidade da medida (PO, 20/09/2026).
              return (
                <button
                  type="button"
                  className={tem ? 'rk__tile' : 'rk__tile rk__tile--vazio'}
                  key={t.key}
                  data-testid={`rk-titulo-${t.key}`}
                  onClick={() => setDestaque({ tipo: 'titulo', key: t.key })}
                  aria-label={`${l.nome}: ver classificação`}
                >
                  {/* A etiqueta substitui o nome (refino final). */}
                  <div className="rk__tile-cab">
                    <Etiqueta nome={l.nome} icone={l.icone as IconName} tom={tem ? TOM_DO_TITULO[t.key] : 'tinta'} className={tem ? 'rk__tile-etiqueta' : 'rk__tile-etiqueta rk__tile-etiqueta--vazia'} />
                    {tem && <img className="rk__tile-coroa" src="/ilustracoes/crown-rank.png" alt="" width={20} height={20} aria-hidden="true" />}
                  </div>
                  <span className="rk__tile-medida">{l.medida}</span>
                  <div className="rk__donos">{donos(t.holders, l.unidade)}</div>
                </button>
              );
            })}
          </div>

          <SectionLabel>MEDALHAS POR TIPO DE GASTO</SectionLabel>
          <Card data-testid="rk-medalhas">
            <ul className="rk__cats">
              {r.categories.map((c) => {
                const l = RANKING_CATEGORY_LABELS[c.category];
                return (
                  <li className="rk__cat" key={c.category}>
                    <button type="button" className="rk__cat-botao" onClick={() => setDestaque({ tipo: 'medalha', category: c.category })} data-testid={`rk-medalha-${c.category}`} aria-label={`${l.nome}: ver classificação`}>
                    <span className="rk__linha-texto">
                      <Etiqueta nome={l.nome} icone={l.icone as IconName} tom={TOM_DA_CATEGORIA[c.category]} />
                      <span className="rk__linha-familia">{l.medida}</span>
                    </span>
                    {c.holders.length === 0 ? (
                      <span className="rk__dono-conta">ninguém ainda</span>
                    ) : (
                      <span className="rk__cat-donos">
                        {c.holders.map((h) => (
                          <span className="rk__cat-dono" key={h.memberId}>
                            <Avatar nome={h.name} cor={corDe(h.memberId)} url={fotoDe(h.memberId)} tamanho={24} />
                            <span className="rk__dono-nome">{primeiroNome(h.name)}</span>
                          </span>
                        ))}
                        <span className="rk__cat-valor mono">{dinheiro(c.holders[0]!.value)}</span>
                      </span>
                    )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>

          {destaque?.tipo === 'titulo' && (() => {
            const t = r.titles.find((x) => x.key === destaque.key);
            const l = RANKING_TITLE_LABELS[destaque.key];
            return t ? (
              <DestaqueSheet nome={l.nome} medida={l.medida} icone={l.icone as IconName} linhas={t.standings} valor={l.unidade} meuMemberId={r.me.memberId} onFechar={() => setDestaque(null)} />
            ) : null;
          })()}
          {destaque?.tipo === 'medalha' && (() => {
            const c = r.categories.find((x) => x.category === destaque.category);
            const l = RANKING_CATEGORY_LABELS[destaque.category];
            return c ? (
              <DestaqueSheet nome={l.nome} medida={l.medida} icone={l.icone as IconName} linhas={c.standings} valor={dinheiro} meuMemberId={r.me.memberId} onFechar={() => setDestaque(null)} />
            ) : null;
          })()}
        </>
      )}
    </section>
  );
}

export type { Ranking };
